import { AuthApiError } from '../errors.js';
/**
 * Builds a typed client bound to a specific API base. The returned object is
 * stateless aside from delegating to `getToken` / `setToken`; safe to memoize
 * once per app.
 */
export function createAuthClient(options) {
    const apiBase = stripTrailingSlash(options.apiBase);
    let refreshInFlight = null;
    async function request(path, requestOptions = {}) {
        const { method = 'GET', body, searchParams, auth = false, signal, isRetry = false } = requestOptions;
        const url = buildUrl(apiBase, path, searchParams);
        const headers = new Headers({ Accept: 'application/json' });
        const sentToken = auth ? options.getToken() : null;
        if (sentToken !== null && sentToken.length > 0) {
            headers.set('Authorization', `Bearer ${sentToken}`);
        }
        let serializedBody;
        if (body !== undefined) {
            headers.set('Content-Type', 'application/json');
            serializedBody = JSON.stringify(body);
        }
        const response = await fetch(url, {
            method,
            headers,
            cache: 'no-store',
            ...(serializedBody === undefined ? {} : { body: serializedBody }),
            ...(signal === undefined ? {} : { signal }),
        });
        const responseText = await response.text();
        if (!response.ok) {
            const envelope = parseErrorEnvelope(responseText, response.status);
            // A 401 on an authed request usually means the short-lived session token expired. Trade
            // the refresh token for a new one and retry once; only when that fails is it the
            // "session ended" signal that sends the host to login.
            if (response.status === 401 && auth) {
                if (!isRetry && (await refreshSessionOnce(sentToken))) {
                    return request(path, { ...requestOptions, isRetry: true });
                }
                options.onUnauthenticated?.();
            }
            throw new AuthApiError(envelope, response.status);
        }
        if (responseText === '')
            return undefined;
        try {
            return JSON.parse(responseText);
        }
        catch {
            throw new AuthApiError({ errorCode: 'INVALID_RESPONSE', message: 'The server returned data that was not valid JSON.' }, response.status);
        }
    }
    /**
     * Persists the token from a fresh `LoginResult` and returns the result for
     * convenience. Centralised so every login-shaped endpoint behaves identically.
     */
    function captureLogin(result) {
        options.setToken(result.sessionToken);
        // A refresh response may omit the refresh token (no rotation) — keep the stored one then.
        if (result.refreshToken !== undefined && result.refreshToken.length > 0) {
            options.setRefreshToken?.(result.refreshToken);
        }
        return result;
    }
    function clearTokens() {
        options.setToken(null);
        options.setRefreshToken?.(null);
    }
    async function refreshSession() {
        const refreshToken = options.getRefreshToken?.() ?? null;
        if (refreshToken === null || refreshToken.length === 0) {
            throw new AuthApiError({ errorCode: fallbackCodeForStatus(401), message: fallbackMessageForStatus(401) }, 401);
        }
        // The refresh token travels in the body, never as a bearer, so it stays out of access logs.
        const result = await request('/auth/session/refresh', {
            method: 'POST',
            body: { refreshToken },
        });
        return captureLogin(result);
    }
    function refreshSessionOnce(failedToken) {
        // Another request already refreshed while this one was in flight: just retry.
        const currentToken = options.getToken();
        if (currentToken !== null && currentToken.length > 0 && currentToken !== failedToken) {
            return Promise.resolve(true);
        }
        const refreshToken = options.getRefreshToken?.() ?? null;
        if (refreshToken === null || refreshToken.length === 0)
            return Promise.resolve(false);
        refreshInFlight ??= refreshSession()
            .then(() => true)
            .catch(() => {
            clearTokens();
            return false;
        })
            .finally(() => {
            refreshInFlight = null;
        });
        return refreshInFlight;
    }
    return {
        async signup(input) {
            const response = await request('/auth/signup', { method: 'POST', body: input });
            if (response.result !== undefined)
                captureLogin(response.result);
            return response;
        },
        async login(input) {
            const outcome = await request('/auth/login', { method: 'POST', body: input });
            if (outcome.status === 'authenticated')
                captureLogin(outcome.result);
            return outcome;
        },
        async initiateOtpLogin(input) {
            return request('/auth/login/otp/initiate', { method: 'POST', body: input });
        },
        async completeOtpLogin(input) {
            const outcome = await request('/auth/login/otp/complete', { method: 'POST', body: input });
            if (outcome.status === 'authenticated')
                captureLogin(outcome.result);
            return outcome;
        },
        async completeMfa(input) {
            const result = await request('/auth/login/mfa/complete', { method: 'POST', body: input });
            return captureLogin(result);
        },
        async logout() {
            try {
                await request('/auth/logout', { method: 'POST', body: {}, auth: true });
            }
            finally {
                // Always clear local state — even if the server call fails the user's
                // intent is "be logged out", and a stale token will fail every
                // subsequent request anyway.
                clearTokens();
            }
        },
        refreshSession,
        refreshSessionOnce,
        async initiatePasswordRecovery(input) {
            return request('/auth/password/recover/initiate', { method: 'POST', body: input });
        },
        async completePasswordRecovery(input) {
            const result = await request('/auth/password/recover/complete', { method: 'POST', body: input });
            return captureLogin(result);
        },
        async changePassword(input) {
            await request('/auth/password/change', { method: 'POST', body: input, auth: true });
        },
        async initiateEmailVerification(input) {
            return request('/auth/email/verify/initiate', { method: 'POST', body: input });
        },
        async completeEmailVerification(input) {
            await request('/auth/email/verify/complete', { method: 'POST', body: input });
        },
        async getMe() {
            return request('/auth/me', { auth: true });
        },
        async updateProfile(input) {
            return request('/auth/me', { method: 'PATCH', body: input, auth: true });
        },
        async getMfaSettings() {
            return request('/auth/me/mfa', { auth: true });
        },
        async updateMfaSettings(input) {
            await request('/auth/me/mfa', { method: 'PUT', body: input, auth: true });
        },
        async listSocialProviders() {
            return request('/auth/social/providers');
        },
        async startSocialLogin({ provider, returnTo }) {
            const path = `/auth/social/${encodeURIComponent(provider)}/start`;
            return request(path, {
                searchParams: { return_to: returnTo },
            });
        },
        async startSocialSignup({ provider, returnTo }) {
            const path = `/auth/social/${encodeURIComponent(provider)}/signup-start`;
            return request(path, {
                searchParams: { return_to: returnTo },
            });
        },
        async exchangeSocialLogin(input) {
            const result = await request('/auth/social/exchange', {
                method: 'POST',
                body: input,
            });
            // captureLogin persists the bearer token to localStorage AND React state. Without
            // this, the OIDC callback page navigates to / before the token is set →
            // ProtectedRoute sees user=null → bounces back to /login. Easy to forget when
            // adding new login-shaped endpoints.
            return captureLogin(result);
        },
        async inspectLinkFlow({ flowId }) {
            return request('/auth/link/inspect', { searchParams: { flowId } });
        },
        async submitLinkPassword(input) {
            const result = await request('/auth/link/password', {
                method: 'POST',
                body: input,
            });
            return captureLogin(result);
        },
        async initiateLinkCode(input) {
            await request('/auth/link/code/initiate', { method: 'POST', body: input });
        },
        async completeLinkCode(input) {
            const result = await request('/auth/link/code/complete', {
                method: 'POST',
                body: input,
            });
            return captureLogin(result);
        },
        // -- Admin -----------------------------------------------------------------
        async adminListUsers(listOptions = {}) {
            return request('/admin/users', {
                auth: true,
                searchParams: { limit: listOptions.limit, cursor: listOptions.cursor },
            });
        },
        async adminGetUser(id) {
            return request(`/admin/users/${encodeURIComponent(id)}`, { auth: true });
        },
        async adminCreateUser(input) {
            return request('/admin/users', { method: 'POST', body: input, auth: true });
        },
        async adminUpdateUser(id, input) {
            return request(`/admin/users/${encodeURIComponent(id)}`, {
                method: 'PATCH',
                body: input,
                auth: true,
            });
        },
        async adminDeleteUser(id) {
            await request(`/admin/users/${encodeURIComponent(id)}`, { method: 'DELETE', auth: true });
        },
        async adminGetUserGroups(id) {
            return request(`/admin/users/${encodeURIComponent(id)}/groups`, { auth: true });
        },
        async adminSetUserGroups(id, groupNames) {
            return request(`/admin/users/${encodeURIComponent(id)}/groups`, {
                method: 'PUT',
                body: { groupNames },
                auth: true,
            });
        },
        async adminAddUserGroup(id, group) {
            return request(`/admin/users/${encodeURIComponent(id)}/groups/${encodeURIComponent(group)}`, { method: 'POST', auth: true });
        },
        async adminRemoveUserGroup(id, group) {
            return request(`/admin/users/${encodeURIComponent(id)}/groups/${encodeURIComponent(group)}`, { method: 'DELETE', auth: true });
        },
        async adminResetUserPassword(id) {
            await request(`/admin/users/${encodeURIComponent(id)}/password/reset`, {
                method: 'POST',
                body: {},
                auth: true,
            });
        },
    };
}
function buildUrl(apiBase, path, searchParams) {
    const url = new URL(path.startsWith('/') ? path.slice(1) : path, `${apiBase}/`);
    if (searchParams !== undefined) {
        for (const [key, value] of Object.entries(searchParams)) {
            if (value === undefined)
                continue;
            url.searchParams.set(key, String(value));
        }
    }
    return url.toString();
}
function stripTrailingSlash(value) {
    return value.endsWith('/') ? value.slice(0, -1) : value;
}
/**
 * Best-effort decode of the API's `{ errorCode, message }` envelope. Falls back
 * to a generic shape so callers always get an `AuthApiError` they can branch on
 * by HTTP status when the body is unparseable.
 */
function parseErrorEnvelope(responseText, httpStatus) {
    if (responseText === '') {
        return { errorCode: fallbackCodeForStatus(httpStatus), message: fallbackMessageForStatus(httpStatus) };
    }
    try {
        const parsed = JSON.parse(responseText);
        const errorCode = typeof parsed.errorCode === 'string' && parsed.errorCode.length > 0
            ? parsed.errorCode
            : fallbackCodeForStatus(httpStatus);
        const message = typeof parsed.message === 'string' && parsed.message.length > 0
            ? parsed.message
            : typeof parsed.error === 'string' && parsed.error.length > 0
                ? parsed.error
                : fallbackMessageForStatus(httpStatus);
        return { errorCode, message };
    }
    catch {
        return { errorCode: fallbackCodeForStatus(httpStatus), message: fallbackMessageForStatus(httpStatus) };
    }
}
function fallbackCodeForStatus(status) {
    if (status === 401)
        return 'AUTHENTICATION_REQUIRED';
    if (status === 403)
        return 'PERMISSION_DENIED';
    if (status === 404)
        return 'NOT_FOUND';
    if (status === 422)
        return 'VALIDATION_ERROR';
    if (status >= 500)
        return 'SERVER_ERROR';
    return 'REQUEST_FAILED';
}
function fallbackMessageForStatus(status) {
    if (status === 401)
        return 'Sign in to continue.';
    if (status === 403)
        return 'You do not have permission to perform that action.';
    if (status === 404)
        return 'The requested resource was not found.';
    if (status >= 500)
        return 'The server is unavailable. Try again in a moment.';
    return `Request failed with status ${status}.`;
}
//# sourceMappingURL=auth-client.js.map